/**
 * Spec 088a — typed API client for admin feature-flag routes.
 *
 *   GET   /api/v1/admin/feature-flags          → { data: FlagRow[] }
 *   PATCH /api/v1/admin/feature-flags/:key     body { enabled: boolean }  → { data: FlagRow }
 *
 * Both routes are gated server-side by `orbit:admin` (see
 * `src/routes/admin-feature-flags.ts`). Same request envelope / error
 * behavior as the 082 rule-ingestion endpoints — thin wrappers over
 * `apiFetch` + `apiGet` so 401 redirect handling stays consistent.
 */
import { apiFetch, apiGet } from '../lib/api.js';

export interface FlagRow {
  key: string;
  enabled: boolean;
  updated_at: string;
  updated_by: string | null;
  notes: string | null;
}

export async function listFeatureFlags(): Promise<FlagRow[]> {
  const res = await apiGet<{ data: FlagRow[] }>('/api/v1/admin/feature-flags');
  return res.data;
}

export async function patchFeatureFlag(
  key: string,
  enabled: boolean,
): Promise<FlagRow> {
  const res = await apiFetch(
    `/api/v1/admin/feature-flags/${encodeURIComponent(key)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      `Patch feature flag failed: ${res.status} ${text.slice(0, 200)}`,
    );
  }
  const json = (await res.json()) as { data: FlagRow };
  if (!json || !json.data || typeof json.data.enabled !== 'boolean') {
    throw new Error('Unexpected response shape from feature-flags PATCH');
  }
  return json.data;
}
